/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import _ from "underscore";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";
import birdata from "../api.js";

var lookups;
var lookupPromise = birdata.lookup().then(x => lookups = x);

const code = 'bushfire';

const BushfireExtension = {
	code: code,
	load: function() {
		return lookupPromise;
	},
	fields: createSurveyFields,
	// Note: this is called before any existing survey data is loaded
	setupSurveyEditing: function(survey) {
		var isEnabled = ko.pureComputed(function() {
			return survey.program() && (survey.program().code === 'birdata' || survey.program().code === 'biby');
		});

		var cachedSurveyFields;
		function surveyFields() {
			if(!cachedSurveyFields) {
				cachedSurveyFields = createSurveyFields();
			}
			return cachedSurveyFields;
		}


		survey.extensionData[code] = {
			fields: ko.pureComputed(function() {
				if(isEnabled()) {
					var f = surveyFields();
					return f;
				} else {
					return null;
				}
			}),
			validations: ko.pureComputed(survey => {
				if(isEnabled()) {
					var fields = _.values(surveyFields());
					var descendants = _.flatten(fields.map(f => f.field.withDescendants()));
					return descendants.map(f => f.value);
				} else {
					return [];
				}
			}),
			enabled: isEnabled
		}
	},
	updateSurveyJSON: function(json, survey) {
		const extensionData = survey.extensionData[code];
		const fields = extensionData.fields();

		if(extensionData.enabled() && fields.bushfireAssessment && fields.bushfireAssessment.field.exists()) {
			var data = {};
			_.values(fields).forEach(f => f.field.save(data))

			json[code] = data.bushfire_assessment || {};
		}
	},
	loadFromJSON: function(json, fields, survey) {
		// const fields = _.values(survey.extensionData[code].fields()) || [];

		var data = {bushfire_assessment: json[code]};
		_.values(fields).forEach(f => f.field.load(data));
	}
}

function createSurveyFields() {
	function siteBurnt() {
		return fields.bushfireAssessment() && fields.bushfireAssessment().siteBurnt();
	}

	function supplementalFoodProvided() {
		return fields.bushfireAssessment() && fields.bushfireAssessment().supplementalFoodProvided();
	}

	var fields = [
		{
			name: "bushfireAssessment",
			type: "compound",
			minCount: 0,
			maxCount: 1,
			fields: [
				{
					name: "siteBurnt",
					type: "boolean",
					label: "Is the survey area burnt?",
					style: "radio",
					required: true,
					validate: "required",
					defaultValue: null
				},
				{
					name: "timeSinceBurntInMonths",
					type: "integer",
					label: "Estimated time since survey area was burnt (if known)",
					units: "months",
					max: 99,
					min: 0,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "areaBurntPercent",
					type: "integer",
					label: "Estimated percentage of survey area burnt (if known)",
					units: "%",
					max: 100,
					min: 0,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "burnIntensity",
					type: "options",
					style: "radio",
					options: lookups.burn_intensity,
					optionsText: 'name',
					label: "Severity",
					hidden: () => siteBurnt() !== true
				},
				{
					name: "distanceToUnburntAreaInKilometres",
					type: "number",
					label: "Estimated distance to nearest unburnt area (if known)",
					units: "km",
					decimalPlaces: 2,
					max: 9999,
					min: 0,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "groundLayerStatus",
					type: "options",
					label: "Ground layer (less than 50 cm)",
					options: lookups.vegetation_fire_status,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "lowerStoreyStatus",
					type: "options",
					label: "Lower storey (50 cm to 3 m)",
					options: lookups.vegetation_fire_status,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "midStoreyStatus",
					type: "options",
					label: "Mid-storey (below canopy and above 3 m)",
					options: lookups.vegetation_fire_status,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "canopyStatus",
					type: "options",
					label: "Canopy layer",
					options: lookups.vegetation_fire_status,
					hidden: () => siteBurnt() !== true
				},
				{
					name: "distanceToBurntAreaInKilometres",
					type: "number",
					label: "Estimated distance to nearest burnt area (if known)",
					units: "km",
					decimalPlaces: 2,
					max: 9999,
					min: 0,
					hidden: () => siteBurnt() !== false
				},


				{
					name: "supplementalFoodProvided",
					type: "boolean",
					label: "Is there supplemental food, water or nest boxes provided in the survey area?",
					style: "radio",
					required: true,
					validate: "required",
					defaultValue: null
				},

				{
					name: "seedProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Seed",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "minceProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Mince",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "otherMeatProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Other meat or dog food",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "nectarProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Nectar mix",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "invertebratesProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Mealworms or other invertebrates",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "fruitProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Fruit",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "breadProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Bread",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "otherFoodProvided",
					type: "text",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "pedestalBathProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Pedestal bath",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "saucerProvidedFrequency",
					type: "options",
					options: lookups.bushfire_feeding_frequency,
					label: "Saucer on ground",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "pondProvided",
					type: "boolean",
					allowNull: true,
					label: "Pond",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "poolProvided",
					type: "boolean",
					allowNull: true,
					label: "Pool",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "otherWaterProvided",
					type: "text",
					hidden: () => supplementalFoodProvided() !== true
				},
				{
					name: "nestBoxSmallCount",
					type: "integer",
					label: "Small boxes (e.g. pardalote)",
					hidden: () => supplementalFoodProvided() !== true,
					defaultValue: 0,
					min: 0,
					max: 999
				},
				{
					name: "nestBoxMediumCount",
					type: "integer",
					label: "Medium boxes (e.g. owlet-nightjar, rosella)",
					hidden: () => supplementalFoodProvided() !== true,
					defaultValue: 0,
					min: 0,
					max: 999
				},
				{
					name: "nestBoxLargeCount",
					type: "integer",
					label: "Large boxes (e.g. galah, duck, owl)",
					hidden: () => supplementalFoodProvided() !== true,
					defaultValue: 0,
					min: 0,
					max: 999
				},
				{
					name: "nestBoxExtraLargeCount",
					type: "integer",
					label: "Extra large boxes (e.g. black-cockatoo)",
					hidden: () => supplementalFoodProvided() !== true,
					defaultValue: 0,
					min: 0,
					max: 999
				},
				{
					name: "nestBoxOther",
					type: "text",
					label: "Other nest boxes",
					hidden: () => supplementalFoodProvided() !== true
				},
			]
		}
	].map(fieldManager.field);

	fields = util.arrayToMap(fields, f => f.field.name);

	return fields;
}

export default BushfireExtension;
